import React, { useEffect, useCallback, useState } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Stack,
    Grid,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Card,
    styled,
    IconButton,
    Button,
    OutlinedInput,
    Chip
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { useFormik } from 'formik';
import { useNavigate, useParams } from "react-router-dom";
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';
import { CommonIcons, icons } from "../../common/CommonIcons";
import { spliceString } from "../../../utils/formatString";

import { useRates, rateTypes, targetTypes as rateTargetTypes } from '../../../hooks/api/useRates';
import { useGroups, groupTypes, targetTypes } from '../../../hooks/api/useGroups';

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';
import { initialValues, initialRowValues, validationSchema, measureTypes, chargeMethods } from "../../../schema/rates/shipping/shippingRuleSchema";
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from "../../../hooks/useTranslations";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function UpdateShippingRule({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@rate", "prompt"] })

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();
    const { isLoading, get, update, calculationTypes } = useRates({ rateType: rateTypes.shippingRule, targetType: targetTypes.shippingRule });
    const { list: listCustomerGroup } = useGroups({ groupType: groupTypes.customer });

    const { isPermissionAllow } = usePermissions(routeType, scopeIds.rate.id, scopeTypes.rate.shippingRule.id)
    const { setSystemLoading } = useAppContextFeatures();

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState()
    const [dataCustomerGroup, setDataCustomerGroup] = useState([])

    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const loadData = useCallback(async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id })
            const { rule, items } = data;

            const rows = items.map((row) => {
                return (
                    {
                        minValue: row.min_value,
                        maxValue: row.max_value,
                        itemRate: row.item_rate,
                    }
                )
            })

            setSavedValues({
                ruleName: rule.rule_name,
                baseFee: rule.base_fee,
                calculationType: rule.calculation_type,
                description: rule.description,
                status: rule?.status === "1",
                measureType: rule.measure_type,
                chargeMethod: rule.charge_method,
                customerGroup: rule.customer_groups,
                rows
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    const onSubmit = async (currentValues) => {

        let items = currentValues.rows.map(item => {
            return {
                min_value: item?.minValue,
                max_value: item?.maxValue,
                item_rate: item?.itemRate,
            }
        })

        try {

            let data = {
                rule: {
                    rule_name: currentValues?.ruleName,
                    calculation_type: currentValues?.calculationType,
                    description: currentValues?.description,
                    base_fee: currentValues?.baseFee,
                    status: currentValues?.status ? 1 : 0,
                },
                props: {
                    measure_type: currentValues?.measureType,
                    charge_method: currentValues?.chargeMethod,
                    customer_groups: currentValues?.customerGroup
                },
                items

            }
            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("settings/shippingRules"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }
    }

    const fetchDataCustomerGroup = async () => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: "id",
                    dir: 0,
                }
            }

            const { data } = await listCustomerGroup(props)
            setDataCustomerGroup(data?.groups)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }
    }

    useEffect(() => {

        loadData(id);
        fetchDataCustomerGroup()

    }, [])

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const handleAddItem = ({ rows, setFieldValue }) => {

        rows.push(initialRowValues);
        setFieldValue('rows', rows)

    }

    const handleRemoveItem = ({ rows, index, setFieldValue }) => {

        const currentLine = rows ? rows.length : 0;

        if (currentLine <= 1) {
            promptMessage(false, t(`itemsAtLeastHave1Row`))
            return false;
        }

        const newRows = rows.filter((i, ind) => ind !== index)

        // rows.remove(index);
        setFieldValue('rows', newRows)

        return true;

    }

    const getCustomerNameByIds = (optionIdArray) => {

        if (!optionIdArray) return []

        let optionNames = [];

        dataCustomerGroup.forEach((e) => {
            if (optionIdArray.includes(parseInt(e.id, 10))) {
                optionNames.push(e.label)
            }
        })

        // console.log('optionIdArray', optionIdArray, dataCustomerGroup, optionNames)

        return optionNames
    }

    const RenderRuleInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('ruleInfo')}
                        subTitle={t('detailsOfShippingRule')}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>

                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"ruleName"}
                                label={t("ruleName")}
                                type="text"
                                variant="outlined"
                                value={values?.ruleName}
                                onChange={handleChange}
                                error={touched.ruleName && Boolean(errors.ruleName)}
                                helperText={touched.ruleName && errors.ruleName}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <FormControlLabel
                                sx={{
                                    marginLeft: '1px'
                                }}
                                control={
                                    <Switch
                                        sx={{
                                            mt: 1
                                        }}
                                        name="status"
                                        checked={Boolean(values?.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values.status) ? t("enabled") : t("disabled")
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.calculationType && Boolean(errors.calculationType)}
                            >
                                <InputLabel id="calculationType">{t('calculationType')}</InputLabel>
                                <Select
                                    labelId="calculationType"
                                    label={t('calculationType')}
                                    name={"calculationType"}
                                    onChange={(event) => {
                                        setFieldValue("calculationType", event.target.value)
                                    }
                                    }
                                    value={values?.calculationType}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >

                                    {
                                        calculationTypes?.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i.id}>{i.display}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.calculationType && errors.calculationType}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"baseFee"}
                                label={t("baseAmount")}
                                type="text"
                                variant="outlined"
                                value={values?.baseFee}
                                onChange={handleChange}
                                error={touched.baseFee && Boolean(errors.baseFee)}
                                helperText={touched.baseFee && errors.baseFee}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"description"}
                                label={t("description")}
                                type="text"
                                multiline
                                rows={2}
                                variant="outlined"
                                value={values?.description ? values?.description : ""}
                                onChange={handleChange}
                                error={touched.description && Boolean(errors.description)}
                                helperText={touched.description && errors.description}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderRateInfo = () => {

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('rateInfo')}
                    // subTitle={'Details of shipping rule'}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>

                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.measureType && Boolean(errors.measureType)}
                            >
                                <InputLabel id="measureType">{t('measureType')}</InputLabel>
                                <Select
                                    labelId="measureType"
                                    label={t("measureType")}
                                    name={"measureType"}
                                    onChange={(event) => {
                                        setFieldValue("measureType", event.target.value)
                                    }
                                    }
                                    value={values.measureType}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >

                                    {
                                        measureTypes.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i.id}>{i.display}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.measureType && errors.measureType}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.chargeMethod && Boolean(errors.chargeMethod)}
                            >
                                <InputLabel id="chargeMethod">{t('chargeMethod')}</InputLabel>
                                <Select
                                    labelId="chargeMethod"
                                    label={t("chargeMethod")}
                                    name={"chargeMethod"}
                                    onChange={(event) => {
                                        setFieldValue("chargeMethod", event.target.value)
                                    }}
                                    value={values?.chargeMethod}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >

                                    {
                                        chargeMethods.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i.id}>{i.display}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.chargeMethod && errors.chargeMethod}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.customerGroup && Boolean(errors.customerGroup)}
                            >
                                <InputLabel id="customerGroup">{t('groupCustomers')}</InputLabel>
                                <Select
                                    labelId="customerGroup"
                                    label={t("groupCustomers")}
                                    name={"customerGroup"}
                                    onChange={(event) => {

                                        console.log('event.target.value', event.target.value)

                                        setFieldValue("customerGroup", event.target.value)
                                    }
                                    }
                                    // value={values.customerGroup}
                                    value={values?.customerGroup ? values.customerGroup : []}
                                    renderValue={(selected) => {

                                        // console.log("Selected", selected, dataCustomerGroup)

                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {
                                                    getCustomerNameByIds(selected).map((i, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={spliceString({ val: i, maxLength: 20 })}
                                                        />
                                                    ))
                                                }
                                            </Box>
                                        )

                                    }}
                                    multiple
                                    input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                                >
                                    {
                                        dataCustomerGroup?.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={parseInt(i.id, 10)}>
                                                    {i.label}
                                                </MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.customerGroup && errors.customerGroup}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderRow = () => {

        const rows = values?.rows;

        return (
            <Box>
                <Box>
                    {
                        rows && rows.length > 0
                        && rows.map((item, index) => {

                            const itemValues = item;
                            let itemTouched = touched?.rows
                            let itemErrors = errors?.rows
                            // const itemTouched = touched?.rows

                            if (itemTouched) {
                                itemTouched = (itemTouched[index] !== undefined) ? itemTouched[index] : {};
                            }

                            if (itemErrors) {
                                itemErrors = (itemErrors[index] !== undefined) ? itemErrors[index] : {};
                            }

                            return (
                                <Grid key={index} item xs={12} sm={12} md={12}>

                                    <Grid container spacing={1} >

                                        <Grid item xs={12} sm={12} md={0.5} alignSelf={"center"}>
                                            <IconButton
                                                sx={{
                                                    mt: 1,
                                                    mr: 2
                                                }}
                                                aria-label="delete"
                                                onClick={() => handleRemoveItem({ rows: values.rows, index, setFieldValue })}
                                            >
                                                <CommonIcons name={icons.remove} />
                                            </IconButton>
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={3.5}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.minValue`}
                                                label={t("minValue")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.minValue ? itemValues.minValue : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.minValue)}
                                                helperText={itemErrors?.minValue}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={3.5}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.maxValue`}
                                                label={t("maxValue")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.maxValue ? itemValues.maxValue : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.maxValue)}
                                                helperText={itemErrors?.maxValue}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={4.5}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.itemRate`}
                                                label={t("itemRate")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.itemRate ? itemValues.itemRate : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.itemRate)}
                                                helperText={itemErrors?.itemRate}
                                            />
                                        </Grid>

                                    </Grid>
                                </Grid>
                            )
                        })
                    }
                </Box>

            </Box>
        )
    }

    const RenderRuleItemInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('ruleItemsInfo')}
                        subTitle={t('detailsOfItemInformation')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    {
                        RenderRow()
                    }
                </Grid>

                <Grid item xs={12} sm={12} md={4} textAlign={"right"}>
                    <Button
                        variant="outlined"
                        sx={{ mt: 2 }}
                        fullWidth
                        startIcon={<CommonIcons name={icons.add} />}
                        onClick={() => handleAddItem({ rows: values.rows, setFieldValue })}
                    >
                        {t('addRule')}
                    </Button>
                </Grid>

            </>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>
                        {
                            RenderRuleInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderRateInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderRuleItemInfo()
                        }

                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateRate })
            }

            <CommonBreadcumbs
                pageTitle={t("updateShippingRule")}
                breadcrumbs={[
                    {
                        displayText: t("shippingRules"),
                        props: {
                            href: getRoute("settings/shippingRules")
                        }
                    },
                    {
                        displayText: t("updateShippingRule"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}